import React, { useEffect, useMemo, useState } from 'react'

const getMachineLabel = machine => {
  if (!machine) return ''

  const name = machine.MachineName || machine.Name || 'Automat'
  const serialNo = machine.SerialNo || machine.MachineSerialNo || ''

  if (!serialNo || String(name).includes(String(serialNo))) return name

  return `${name} ${serialNo}`
}

const ImportExtendedModal = ({
  show,
  currentMachine,
  machines = [],
  selectedMachines = [],
  isLoading = false,
  isImporting = false,
  onAddMachine,
  onRemoveMachine,
  onImportCurrent,
  onImportMany,
  onClose,
}) => {
  const [lookupValue, setLookupValue] = useState('')

  useEffect(() => {
    if (show) {
      setLookupValue('')
    }
  }, [show])

  const selectedMachineIds = useMemo(
    () => new Set(selectedMachines.map(machine => Number(machine.MachineId))),
    [selectedMachines]
  )

  const availableMachines = useMemo(
    () => machines.filter(machine => !selectedMachineIds.has(Number(machine.MachineId))),
    [machines, selectedMachineIds]
  )

  if (!show) return null

  const addMachine = rawValue => {
    const value = String(rawValue || '').trim()

    if (!value) return

    const normalizedValue = value.toLocaleLowerCase('pl-PL')
    const machine = availableMachines.find(item => {
      const label = getMachineLabel(item).toLocaleLowerCase('pl-PL')
      const serialNo = String(item.SerialNo || '').toLocaleLowerCase('pl-PL')
      const name = String(item.MachineName || item.Name || '').toLocaleLowerCase('pl-PL')

      return label === normalizedValue || serialNo === normalizedValue || name === normalizedValue
    })

    if (!machine) return

    onAddMachine(machine)
    setLookupValue('')
  }

  const handleLookupChange = event => {
    const value = event.target.value
    setLookupValue(value)
    addMachine(value)
  }

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Import produktów</h5>
            <button
              type="button"
              className="close"
              aria-label="Zamknij"
              disabled={isImporting}
              onClick={onClose}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <button
              type="button"
              className="btn btn-light border w-100 d-flex align-items-center text-left p-3 mb-3"
              disabled={isLoading || isImporting || !currentMachine}
              onClick={onImportCurrent}
            >
              <i className="fas fa-file-import text-primary fa-lg mr-3" />
              <span>
                <span className="d-block font-weight-bold poppins-font">Importuj na ten automat</span>
                {currentMachine && (
                  <small className="text-muted">{getMachineLabel(currentMachine)}</small>
                )}
              </span>
            </button>

            <div className="border rounded p-3">
              <div className="d-flex align-items-center mb-3">
                <i className="fas fa-file-import text-primary fa-lg mr-3" />
                <span className="font-weight-bold poppins-font">Importuj na wiele automatów</span>
              </div>

              {isLoading ? (
                <div className="text-center py-3">
                  <i className="fas fa-spinner fa-spin mr-2" />
                  Ładowanie automatów...
                </div>
              ) : (
                <>
                  <label htmlFor="extendedImportMachineLookup" className="font-weight-bold">
                    Wybierz automaty
                  </label>
                  <input
                    id="extendedImportMachineLookup"
                    className="form-control"
                    type="text"
                    list="extendedImportMachineOptions"
                    placeholder="Szukaj..."
                    value={lookupValue}
                    disabled={isImporting}
                    onChange={handleLookupChange}
                    onBlur={() => addMachine(lookupValue)}
                  />

                  <datalist id="extendedImportMachineOptions">
                    {availableMachines.map(machine => (
                      <option
                        key={machine.MachineId}
                        value={getMachineLabel(machine)}
                      />
                    ))}
                  </datalist>

                  {machines.length === 0 && (
                    <small className="d-block text-muted mt-2 ml-1">
                      Brak automatów do wyboru
                    </small>
                  )}

                  <div
                    className="mt-3 custom-scroll"
                    style={{
                      height: '120px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                  >
                    {selectedMachines.length === 0 ? (
                      <div className="h-100 d-flex align-items-center">
                        <small className="text-muted"></small>
                      </div>
                    ) : (
                      selectedMachines.map(machine => (
                        <div key={machine.MachineId} className="mb-2">
                          <span className="badge badge-primary p-2 d-inline-flex align-items-center">
                            {getMachineLabel(machine)}
                            <button
                              type="button"
                              className="ml-2 p-0 border-0 bg-transparent text-white"
                              aria-label={`Usuń ${getMachineLabel(machine)}`}
                              disabled={isImporting}
                              onClick={() => onRemoveMachine(machine.MachineId)}
                            >
                              &times;
                            </button>
                          </span>
                        </div>
                      ))
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary btn-sm mt-2"
                    disabled={selectedMachines.length === 0 || isImporting}
                    onClick={onImportMany}
                  >
                    <i className="fas fa-file-import mr-2" />
                    Wybierz plik i importuj
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={isImporting}
              onClick={onClose}
            >
              Anuluj
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImportExtendedModal